import React from 'react';
import SEOHead from '@/components/SEOHead';

const LAST_UPDATED = 'July 17, 2026';
const CONTACT_EMAIL = 'privacy@builderslab.net';

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function DataRow({ name, purpose, retention }) {
  return (
    <tr className="border-b border-zinc-800/60">
      <td className="py-3 pr-4 text-white font-medium text-sm align-top">{name}</td>
      <td className="py-3 pr-4 text-zinc-400 text-sm align-top">{purpose}</td>
      <td className="py-3 text-zinc-500 text-sm align-top whitespace-nowrap">{retention}</td>
    </tr>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Privacy Policy"
        description="Read BuildersLab's privacy policy. Learn how we collect, use, and protect your personal data in compliance with GDPR and global privacy standards."
        ogUrl="https://builderslab.net/PrivacyPolicy"
        noIndex={false}
      />
      <div className="max-w-4xl mx-auto px-6">

        {/* Header */}
        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Privacy Policy</h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            At BuildersLab, your privacy is foundational — not an afterthought. This policy explains exactly
            what personal data we collect, why we collect it, how long we keep it, and what rights you have
            over it. We believe you deserve complete transparency.
          </p>
        </div>

        <div
         
         
         
        >

          <Section title="1. Who We Are">
            <p>
              BuildersLab is an AI-powered platform for building websites and digital products, operated
              by <strong className="text-white">BuildersLab Limited</strong> (RC 9689278), a private company
              limited by shares incorporated in the Federal Republic of Nigeria under the Companies and
              Allied Matters Act 2020, with its registered office at Catherine Rest House Rd, Government
              Station, Nsukka, Enugu State, Nigeria ("we", "us", "our"). Our service is accessible
              at <strong className="text-white">builderslab.net</strong>.
            </p>
            <p>
              BuildersLab Limited is the data controller for the personal data described in this policy,
              which we process in accordance with the Nigeria Data Protection Act 2023 (NDPA) and, where
              applicable, the EU/UK General Data Protection Regulation (GDPR).
            </p>
            <p>
              For privacy-related enquiries, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </Section>

          <Section title="2. Every Data Point We Collect">
            <p>
              The table below lists every category of personal data we collect, the exact reason we collect
              it, and how long we retain it. We do not collect data that is not listed here.
            </p>

            <div className="overflow-x-auto rounded-xl border border-zinc-800 mt-6">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-zinc-700 bg-zinc-900/60">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Data Point</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Why We Collect It</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Retention</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60 px-4">
                  <DataRow
                    name="Email address"
                    purpose="Account creation, authentication, service notifications, password resets, and transactional emails."
                    retention="Until account deletion + 30 days"
                  />
                  <DataRow
                    name="Password (hashed)"
                    purpose="Authenticating your identity when signing in with email/password. We store only a bcrypt hash — never your plaintext password."
                    retention="Until account deletion"
                  />
                  <DataRow
                    name="First & last name"
                    purpose="Personalising your dashboard experience and addressing you in communications."
                    retention="Until account deletion + 30 days"
                  />
                  <DataRow
                    name="Google profile data (OAuth)"
                    purpose="When you sign in with Google, we receive your name, email, and profile picture from Google to create or link your account."
                    retention="Until account deletion + 30 days"
                  />
                  <DataRow
                    name="Profile avatar / photo"
                    purpose="Displaying your identity within the platform UI."
                    retention="Until you remove it or delete your account"
                  />
                  <DataRow
                    name="IP address"
                    purpose="Security monitoring (detecting unauthorised access, rate-limiting abuse, fraud prevention) and approximate geolocation for compliance."
                    retention="90 days in server logs"
                  />
                  <DataRow
                    name="Device type & screen resolution"
                    purpose="Optimising the UI rendering and understanding how users access the platform (desktop vs mobile vs tablet)."
                    retention="24 months (aggregated)"
                  />
                  <DataRow
                    name="Browser type & version"
                    purpose="Diagnosing compatibility issues and ensuring the platform works across supported browsers."
                    retention="24 months (aggregated)"
                  />
                  <DataRow
                    name="Operating system"
                    purpose="Debugging platform-specific issues and ensuring cross-OS compatibility."
                    retention="24 months (aggregated)"
                  />
                  <DataRow
                    name="Pages visited & navigation paths"
                    purpose="Understanding how users navigate the product to improve UX and identify friction points."
                    retention="24 months"
                  />
                  <DataRow
                    name="Feature usage events"
                    purpose="Identifying which features are used most/least to prioritise engineering roadmap decisions."
                    retention="24 months"
                  />
                  <DataRow
                    name="Session duration & activity timestamps"
                    purpose="Understanding engagement patterns and detecting anomalous account activity for security purposes."
                    retention="12 months"
                  />
                  <DataRow
                    name="AI prompts you enter"
                    purpose="Processing your request through the AI generation engine to produce website output. Prompts may also be used (in anonymised form) to fine-tune and improve our AI models."
                    retention="Until account deletion"
                  />
                  <DataRow
                    name="Generated website content (HTML / CSS / JS)"
                    purpose="Storing your generated websites in your dashboard so you can access, preview, share, or export them."
                    retention="Until you delete the website or your account"
                  />
                  <DataRow
                    name="Credits balance & transaction history"
                    purpose="Enforcing usage limits, preventing abuse, and providing you with a usage history."
                    retention="Until account deletion + 7 years (financial records)"
                  />
                  <DataRow
                    name="Subscription plan & status"
                    purpose="Gating features by plan tier and correctly billing your account."
                    retention="Until account deletion + 7 years (financial records)"
                  />
                  <DataRow
                    name="Payment provider references"
                    purpose="Linking your BuildersLab account to your payment records (e.g. Flutterwave transaction references) for subscription management and support. We do not store card numbers — our payment providers hold payment method data."
                    retention="Until account deletion + 7 years"
                  />
                  <DataRow
                    name="Payment & billing history"
                    purpose="Providing you with receipts, resolving billing disputes, and complying with tax and accounting regulations."
                    retention="7 years (legal requirement)"
                  />
                  <DataRow
                    name="Referral code used"
                    purpose="Attributing referral credit to the referring user and applying any signup bonuses."
                    retention="12 months post-signup"
                  />
                  <DataRow
                    name="Session tokens / cookies"
                    purpose="Maintaining your logged-in session across browser tabs and page refreshes. We use signed JWTs issued by our own authentication service."
                    retention="30 days or until logout"
                  />
                  <DataRow
                    name="Error & crash logs"
                    purpose="Identifying and fixing bugs and unexpected behaviour in the platform."
                    retention="90 days"
                  />
                  <DataRow
                    name="Support communications"
                    purpose="Resolving your support requests and maintaining a record of previous interactions to provide better assistance."
                    retention="3 years"
                  />
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="3. Legal Basis for Processing">
            <p>We process your personal data on the following legal bases under applicable data protection law:</p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Contract performance', 'Processing your prompts, storing generated output, and managing your account are necessary to deliver the service you signed up for.'],
                ['Legitimate interests', 'Security monitoring, fraud prevention, analytics, and product improvement — provided these interests are not overridden by your privacy rights.'],
                ['Legal obligation', 'Retaining financial records for 7 years to comply with tax and accounting law.'],
                ['Consent', 'Where we use your data for AI model improvement, we rely on your consent, which you may withdraw at any time by contacting us.'],
              ].map(([basis, desc]) => (
                <li key={basis} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{basis}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="4. Cookies & Tracking">
            <p>We use session cookies for authentication purposes:</p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Session tokens', 'Authentication cookies required to keep you logged in. These are essential for the platform to function and cannot be disabled.'],
              ].map(([type, desc]) => (
                <li key={type} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{type}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="5. Third-Party Services We Use">
            <p>
              We share minimal data with the following trusted third-party processors to operate the platform.
              Each processor has signed Data Processing Agreements with us.
            </p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Hostinger (Hostinger International Ltd.)', 'Cloud hosting, database, and storage. Your account data, generated content, and uploaded assets are stored on Hostinger-managed infrastructure.'],
                ['Flutterwave (Flutterwave Technology Solutions Ltd.)', 'Payment processing and subscription billing — cards, mobile money and bank transfers. Card details and payment credentials are held exclusively by Flutterwave, which is PCI-DSS compliant and licensed by the Central Bank of Nigeria.'],
                ['NOWPayments (NOWPayments OÜ)', 'Cryptocurrency payment processing for customers who choose to pay in crypto. Wallet transactions are processed by NOWPayments; we never hold your private keys.'],
                ['Anthropic (Anthropic, PBC)', 'AI generation — your prompts are transmitted to the Claude API to produce website code. Anthropic\'s API usage policy applies.'],
              ].map(([name, desc]) => (
                <li key={name} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{name}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">
              We do not sell your personal data to any third party. We do not share your data with advertisers.
            </p>
          </Section>

          <Section title="6. International Data Transfers">
            <p>
              Our infrastructure is primarily based in the United States. If you are located in the European
              Economic Area (EEA), United Kingdom, or another region with data transfer restrictions, your
              data may be transferred to and processed in the US. We rely on Standard Contractual Clauses
              (SCCs) and adequacy decisions where applicable to ensure your data receives equivalent protection.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>Depending on your jurisdiction, you have the following rights over your personal data:</p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Access: Request a copy of all personal data we hold about you.',
                'Rectification: Correct inaccurate or incomplete data.',
                'Erasure ("right to be forgotten"): Request deletion of your data, subject to legal retention obligations.',
                'Restriction: Ask us to pause processing your data in certain circumstances.',
                'Portability: Receive your data in a structured, machine-readable format.',
                'Objection: Object to processing based on legitimate interests.',
                'Withdraw consent: Where processing is based on consent, withdraw it at any time without affecting prior lawful processing.',
                'Lodge a complaint: File a complaint with your local data protection authority.',
              ].map(right => (
                <li key={right} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{right}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">
              To exercise any of these rights, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors">
                {CONTACT_EMAIL}
              </a>{' '}
              with the subject line "Data Request". We will respond within 30 days.
            </p>
          </Section>

          <Section title="8. Data Security">
            <p>
              We employ the following technical and organisational security measures to protect your data:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'All data in transit is encrypted using TLS 1.2 or higher.',
                'All data at rest is encrypted using AES-256.',
                'Passwords are hashed using bcrypt with a per-user salt — we cannot recover your password.',
                'Authentication tokens are signed JWTs issued by our own authentication service.',
                'Access to production data is restricted to authorised personnel only, under the principle of least privilege.',
                'We conduct periodic security reviews and vulnerability assessments.',
                'Payment data is never stored on our servers — it is tokenised and held by our PCI-DSS-compliant payment providers.',
              ].map(measure => (
                <li key={measure} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{measure}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              BuildersLab is not directed at individuals under the age of 16. We do not knowingly collect
              personal data from children. If you believe a child has provided us with personal data, please
              contact us immediately at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors">
                {CONTACT_EMAIL}
              </a>{' '}
              and we will delete it promptly.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy periodically. When we make material changes, we will notify
              you via email and/or a prominent notice on the platform at least 14 days before the changes
              take effect. The "Last updated" date at the top of this page reflects the most recent revision.
              Continued use of BuildersLab after the effective date constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data
              practices, please contact our Privacy Team:
            </p>
            <div className="mt-4 p-5 bg-zinc-900 border border-zinc-800 rounded-xl space-y-2">
              <p className="text-white font-semibold">BuildersLab Limited (RC 9689278) — Privacy Team</p>
              <p className="text-zinc-500 text-sm">Catherine Rest House Rd, Government Station, Nsukka, Enugu State, Nigeria</p>
              <p>
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-2">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="text-zinc-500 text-sm">Response time: within 5 business days.</p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
